import React, { useMemo } from 'react';

function microBackgroundPosition(col, row, cols, rows) {
  const x = cols <= 1 ? 0 : (col / (cols - 1)) * 100;
  const y = rows <= 1 ? 0 : (row / (rows - 1)) * 100;
  return `${x}% ${y}%`;
}

function buildPlayerNames(playersOnPiece) {
  const seenUserIds = new Set();
  const names = [];

  (playersOnPiece || []).forEach((player) => {
    const userId = String(player?.userId ?? '').trim();
    if (userId) {
      if (seenUserIds.has(userId)) return;
      seenUserIds.add(userId);
    }

    const name = String(player?.name ?? '').trim();
    if (!name) return;

    if (!userId && names.includes(name)) return;

    names.push(name);
  });

  return names;
}

export default function LivePuzzlePiece({
  src,
  pieceId,
  userState = null,
  playersOnPiece = [],
  tileRows = 3,
  tileCols = 3,
  isMine = false,
  commaSeparateNames = false,
}) {
  const rows = Math.max(1, Math.floor(Number(userState?.microRows)) || tileRows);
  const cols = Math.max(1, Math.floor(Number(userState?.microCols)) || tileCols);
  const totalTiles = Math.max(1, rows * cols);
  const completed = Boolean(userState?.completed);

  const playerNames = useMemo(
    () => buildPlayerNames(playersOnPiece),
    [playersOnPiece]
  );

  const playerLabel = playerNames.join('\n');

  const placedSet = useMemo(() => {
    const placed = Array.isArray(userState?.placedTiles) ? userState.placedTiles : [];
    return new Set(placed.map((n) => Math.floor(Number(n))).filter((n) => n >= 0));
  }, [userState?.placedTiles]);

  const placedCount = completed ? totalTiles : placedSet.size;
  const progress = Math.min(100, Math.round((placedCount / totalTiles) * 100));
  const hasPlayer = Boolean(userState);

  return (
    <div
      className={`live-puzzle-piece${isMine ? ' live-puzzle-piece--mine' : ''}${
        completed ? ' live-puzzle-piece--done' : ''
      }${hasPlayer ? ' live-puzzle-piece--active' : ''}${
        playerLabel ? ' live-puzzle-piece--named' : ''
      }`}
      title={
        playerLabel
          ? `${playerLabel} — Part ${pieceId} — ${progress}%`
          : `Part ${pieceId} — waiting for a player`
      }
    >
      <div className="live-puzzle-piece-visual">
        {!hasPlayer ? (
          <div className="live-puzzle-piece-waiting">
            <img src={src} alt="" className="live-puzzle-piece-waiting-img" aria-hidden />
            <div className="live-puzzle-piece-waiting-mask" aria-hidden />
            <span className="live-puzzle-piece-waiting-label">Part {pieceId}</span>
          </div>
        ) : completed ? (
          <img
            src={src}
            alt=""
            className="live-puzzle-piece-complete-img"
            aria-hidden
          />
        ) : (
          <div
            className="live-puzzle-piece-micro-grid"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: totalTiles }, (_, index) => {
              const row = Math.floor(index / cols);
              const col = index % cols;
              const isPlaced = completed || placedSet.has(index);

              return (
                <div
                  key={`micro-${pieceId}-${index}`}
                  className={`live-puzzle-piece-micro${isPlaced ? ' live-puzzle-piece-micro--placed' : ''}`}
                >
                  {isPlaced ? (
                    <div
                      className="live-puzzle-piece-micro-img"
                      style={{
                        backgroundImage: `url("${src}")`,
                        backgroundSize: `${cols * 100}% ${rows * 100}%`,
                        backgroundPosition: microBackgroundPosition(col, row, cols, rows),
                      }}
                      aria-hidden
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {hasPlayer ? (
          <span className="live-puzzle-piece-badge">
            {completed ? 'Done' : `${progress}%`}
          </span>
        ) : null}
      </div>

      {playerNames.length > 0 ? (
        <div className="live-puzzle-piece-meta">
          <div
            className={`live-puzzle-piece-names${
              commaSeparateNames ? ' live-puzzle-piece-names--comma' : ''
            }`}
          >
            {commaSeparateNames ? (
              <span className="live-puzzle-piece-name live-puzzle-piece-name--comma">
                {playerNames.join(', ')}
              </span>
            ) : (
              playerNames.map((name, nameIndex) => (
                <span
                  key={`${pieceId}-${nameIndex}-${name}`}
                  className="live-puzzle-piece-name"
                >
                  {name}
                </span>
              ))
            )}
          </div>
          <span className="live-puzzle-piece-part">Part {pieceId}</span>
        </div>
      ) : null}
    </div>
  );
}
